import React, { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useLanguage } from '../LanguageContext'
import { translations } from '../translations'
import "../styles/products.css"

// ── Shared assets ─────────────────────────────────────────────
import logo from '../pictures/logonav.png'

// ── Default image ─────────────────────────────────────────────
import gymMat from '../pictures/gym matt.jpeg'

// VeldFarm® products
import veldfarmHero from "../pictures/veldfarmh.jpg"
import spr16 from "../pictures/c1.png"
import spr18 from "../pictures/dd.png"
import spf20 from "../pictures/ss1.jpg"

// VeldFit® products
import veldfitHero from "../pictures/gymflooringsveld.jpg"
import spr12 from "../pictures/gym matt.jpeg"
import spr14 from "../pictures/b2.png"

// VeldGuard® products
import veldguardHero from "../pictures/h2.jpg"
import spr02 from "../pictures/ss2.jpg"
import spr08 from "../pictures/a4.png"

// VeldHome® products
import veldhomeHero from "../pictures/commercialmatts.jpg"
import spr22 from "../pictures/a2.png"
import spr65 from "../pictures/d6.png"
import spr03 from "../pictures/h5.jpg"

// ── Intersection observer hook ────────────────────────────────
function useInView(threshold = 0.08) {
  const ref = useRef(null)
  const [inView, setInView] = useState(false)
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setInView(true) },
      { threshold }
    )
    if (ref.current) obs.observe(ref.current)
    return () => obs.disconnect()
  }, [threshold])
  return [ref, inView]
}

// ── Page-level static UI strings (nav, header, spec keys, badges) ──
// Add these keys to your translations.js under translations[lang].products
// Expected keys (all under t.products):
//   page_brand, page_eyebrow, page_title, page_sub,
//   spec_material, spec_surface, spec_thickness, spec_application,
//   spec_uv, spec_fire, spec_colours,
//   badge_recyclable, badge_eco, badge_antislip, badge_drainage,
//   badge_easy_clean, badge_interlocking, badge_anti_fatigue,
//   badge_uv, badge_25mm, badge_hygienic, badge_cobble,
//   badge_oil_nitrile, badge_fire_option, badge_connectable,
//   badge_welding, badge_heavy_duty, badge_28mm, badge_multi,
//   badge_coir, badge_biodegradable, badge_scraping,
//   badge_colours, badge_weather,
//   enquire, view_specs, hide_specs, tech_specs, sizes_weights, size, weight

// ── All product data ────────────────────────────────────────────
// Spec keys and badge keys are now language-token keys resolved at render time.
// We use token arrays so the translation lookup happens in the component,
// not at data-definition time (which would freeze the language at module load).

const sections = [
  {
    id: 'veldfarm',
    image: veldfarmHero,
    // category / subtitle / intro: one key per language
    category:   { en: 'VeldFarm® — Cattle & Stable Flooring', ar: 'VeldFarm® — أرضيات الماشية والحظائر', de: 'VeldFarm® — Rinder- und Stallböden',         nl: 'VeldFarm® — Rund- en Stalvloeren' },
    subtitle:   { en: 'Animal Welfare',                        ar: 'رعاية الحيوان',                          de: 'Tierschutz',                                   nl: 'Dierenwelzijn' },
    intro:      {
      en: 'Built for the barn. Designed for animal welfare. VeldFarm mats reduce slipping, stress and limb injuries in cattle, horses and livestock — proven to increase milk yield and improve herd health in European dairy operations.',
      ar: 'مصممة للحظيرة وفقاً لرعاية الحيوان. تقلل حصائر VeldFarm من الانزلاق والإجهاد وإصابات الأطراف في الماشية والخيول والثروة الحيوانية — ثبت أنها تزيد من إنتاجية الألبان وتحسن صحة الثروة الحيوانية.',
      de: 'Gebaut für die Scheune. Entworfen für Tierschutz. VeldFarm-Matten reduzieren Ausrutscher, Stress und Gliedmaßenverletzungen bei Rindern, Pferden und Vieh — nachgewiesen, um die Milchleistung zu erhöhen und die Herdengesundheit zu verbessern.',
      nl: 'Gebouwd voor de schuur. Ontworpen voor dierenwelzijn. VeldFarm-matten verminderen uitglijden, stress en letsel aan ledematen in runderen, paarden en vee — aangetoond om melkopbrengst te verhogen en veegezondheid te verbeteren.',
    },
    products: [
      {
        name:  { en: 'Studded Comfort — Stable Mat', ar: 'Studded Comfort — حصيرة الحظيرة', de: 'Studded Comfort — Stallmatte', nl: 'Studded Comfort — Stalmatte' },
        code: 'SPR 16',
        image: spr16,
        desc: {
          en: 'Anti-slip top surface provides comfort and safety for horses and cattle. Bottom studs give extra cushioning and facilitate easy drainage of urine and water. Easy to install, maintain and clean. Ideal for horse stables and cow sheds.',
          ar: 'السطح العلوي المضاد للانزلاق يوفر الراحة والأمان للخيول والماشية. تعطي الدبابيس السفلية توسيد إضافي وتسهل تصريف البول والماء بسهولة. سهلة التثبيت والصيانة والتنظيف. مثالية لحظائر الخيل وحظائر الأبقار.',
          de: 'Die rutschfeste Oberseite bietet Komfort und Sicherheit für Pferde und Rinder. Bodenstifte bieten zusätzliche Polsterung und erleichtern die Entwässerung von Urin und Wasser. Leicht zu installieren, zu warten und zu reinigen. Ideal für Pferdeställe und Kuhställe.',
          nl: 'Het anti-slip oppervlak biedt comfort en veiligheid voor paarden en runderen. Bodemstiften bieden extra demping en vergemakkelijken afwatering van urine en water. Gemakkelijk te installeren, onderhouden en schoon te maken. Ideaal voor paardenboxen en koeienboxen.',
        },
        // specKeys: array of [translationKey, value-per-lang | string]
        specs: [
          { key: 'spec_material',    value: { en: 'Natural & recycled rubber blend', ar: 'مزيج مطاط طبيعي ومعاد تدويره', de: 'Natur- & Recyclingkautschukmischung', nl: 'Natuur- en gerecycled rubbermengsel' } },
          { key: 'spec_surface',     value: { en: 'Amebic / Hammer blow top',        ar: 'سطح أميبي / ضربة مطرقة',         de: 'Amöben-/Hammerschlag-Oberfläche',   nl: 'Amebisch / Hamerslagtop' } },
          { key: 'spec_thickness',   value: { en: '17mm', ar: '17 ملم', de: '17 mm', nl: '17 mm' } },
          { key: 'spec_application', value: { en: 'Horse stables, cow sheds, dairy farms', ar: 'حظائر الخيل وحظائر الأبقار والمزارع الألبانية', de: 'Pferdeställe, Kuhställe, Milchviehbetriebe', nl: 'Paardenboxen, koeienstallen, melkveebedrijven' } },
        ],
        sizes: [
          ['1.22m × 1.83m × 17mm', '36 kg'],
          ['1.22m × 0.91m × 17mm', '18 kg'],
        ],
        badgeKeys: ['badge_recyclable', 'badge_eco', 'badge_antislip', 'badge_drainage', 'badge_easy_clean'],
      },
      {
        name:  { en: 'Tough-Tread — Interlocking Stable Mat', ar: 'Tough-Tread — حصيرة الحظيرة قابلة التشابك', de: 'Tough-Tread — Verriegelte Stallmatte', nl: 'Tough-Tread — Insteekbare Stalmatte' },
        code: 'SPR 18',
        image: spr18,
        desc: {
          en: 'Interlocking version of the stable mat, featuring grooved bottom pattern and two top design options — Amebic or Hammer Blow. Interlocking ramps are also available for seamless coverage across the entire stable floor.',
          ar: 'نسخة قابلة للتشابك من حصيرة الحظيرة، مع نمط قاع محفور وخيارات تصميم علوية — Amebic أو Hammer Blow. توجد أيضاً منحدرات قابلة للتشابك لتغطية سلسة في جميع أنحاء أرضية الحظيرة.',
          de: 'Verriegelbare Version der Stallmatte mit gerilltem Bodenmuster und zwei Obendesign-Optionen — Amebic oder Hammer Blow. Verriegelbare Rampen sind auch für eine nahtlose Abdeckung des gesamten Stallbodens erhältlich.',
          nl: 'Verriegelbare versie van de stalmatte, met gegroefd bodempatroon en twee bovenaanzicht-opties — Amebic of Hammer Blow. Verriegelbare hellingen zijn ook beschikbaar voor naadloze bedekking van de volledige stalbasis.',
        },
        specs: [
          { key: 'spec_material',    value: { en: 'Natural & recycled rubber blend', ar: 'مزيج مطاط طبيعي ومعاد تدويره', de: 'Natur- & Recyclingkautschukmischung', nl: 'Natuur- en gerecycled rubbermengsel' } },
          { key: 'spec_surface',     value: { en: 'Amebic / Hammer blow',            ar: 'أميبي / ضربة مطرقة',             de: 'Amöben-/Hammerschlag',               nl: 'Amebisch / Hamerslagtop' } },
          { key: 'spec_thickness',   value: { en: '17mm', ar: '17 ملم', de: '17 mm', nl: '17 mm' } },
          { key: 'spec_application', value: { en: 'Horse stables, cow sheds',        ar: 'حظائر الخيل وحظائر الأبقار',     de: 'Pferdeställe, Kuhställe',           nl: 'Paardenboxen, koeienstallen' } },
        ],
        sizes: [
          ['1.22m × 1.83m × 17mm', '36 kg'],
        ],
        badgeKeys: ['badge_recyclable', 'badge_eco', 'badge_interlocking', 'badge_antislip', 'badge_anti_fatigue'],
      },
      {
        name:  { en: 'Cubicle Cow Mat', ar: 'حصيرة البقرة في الكشك', de: 'Liegeboxmatte für Kühe', nl: 'Ligboxmat voor koeien' },
        code: 'SPF 20',
        image: spf20,
        desc: {
          en: 'Designed specifically for dairy cubicles. Cow productivity is directly linked to comfort — these 25mm thick mats protect cows from hard cold floors, reduce slipping risk and provide sterile, hygienic bedding that is easy to clean. Proven to increase milk yield.',
          ar: 'مصممة خصيصاً لأكشاك الألبان. إنتاجية البقرة مرتبطة مباشرة بالراحة — هذه الحصائر بسماكة 25 ملم تحمي الأبقار من الأرضيات الباردة الصعبة وتقلل من خطر الانزلاق وتوفر فراشاً معقماً وصحياً سهل التنظيف. ثبت أنها تزيد إنتاجية الألبان.',
          de: 'Speziell für Kuhkubikeln ausgelegt. Die Produktivität von Kühen ist direkt mit Komfort verbunden — diese 25 mm dicken Matten schützen Kühe vor harten, kalten Böden, reduzieren das Ausrutschrisiko und bieten sterile, hygienische Bettwäsche, die leicht zu reinigen ist.',
          nl: 'Speciaal ontworpen voor melkkuubicles. Koeproductiviteit is rechtstreeks gekoppeld aan comfort — deze 25 mm dikke matten beschermen koeien tegen harde koude vloeren, verminderen het uitglijdrisico en bieden steriel, hygiënisch beddengoed.',
        },
        specs: [
          { key: 'spec_material',    value: { en: 'Natural & recycled rubber blend', ar: 'مزيج مطاط طبيعي ومعاد تدويره', de: 'Natur- & Recyclingkautschukmischung', nl: 'Natuur- en gerecycled rubbermengsel' } },
          { key: 'spec_surface',     value: { en: 'Anti-slip cobble',                ar: 'سطح حجري مضاد للانزلاق',          de: 'Rutschfestes Kopfsteinpflaster',     nl: 'Anti-slip keien' } },
          { key: 'spec_thickness',   value: { en: '25mm', ar: '25 ملم', de: '25 mm', nl: '25 mm' } },
          { key: 'spec_uv',          value: { en: 'Yes (black)', ar: 'نعم (أسود)', de: 'Ja (schwarz)', nl: 'Ja (zwart)' } },
          { key: 'spec_application', value: { en: 'Dairy cubicles, cow barns',       ar: 'أكشاك الألبان وحظائر الأبقار',    de: 'Milchkubikeln, Kuhställe',           nl: 'Melkkuubicles, koeienstallen' } },
        ],
        sizes: [],
        badgeKeys: ['badge_recyclable', 'badge_eco', 'badge_cobble', 'badge_25mm', 'badge_hygienic'],
      },
    ],
  },
  {
    id: 'veldfit',
    image: veldfitHero,
    category: { en: 'VeldFit® — Gym & Play Flooring', ar: 'VeldFit® — أرضيات الألعاب والصالات الرياضية', de: 'VeldFit® — Fitness- und Spielböden', nl: 'VeldFit® — Fitness- en Speelvloeren' },
    subtitle:  { en: 'Performance & Comfort', ar: 'الأداء والراحة', de: 'Leistung & Komfort', nl: 'Prestatie & Comfort' },
    intro: {
      en: 'Performance underfoot. VeldFit rubber tiles provide shock absorption, anti-slip grip and anti-fatigue comfort for fitness centers, playgrounds, training halls and sports facilities across Europe.',
      ar: 'الأداء تحت القدمين. توفر بلاط VeldFit المطاطي امتصاص الصدمات والقبضة المضادة للانزلاق والراحة المضادة للإرهاق لمراكز اللياقة البدنية والملاعب ومراكز التدريب والمرافق الرياضية في جميع أنحاء أوروبا.',
      de: 'Leistung unter den Füßen. VeldFit-Gummifliesen bieten Stoßdämpfung, rutschfesten Grip und Anti-Ermüdungs-Komfort für Fitnesscenter, Spielplätze, Trainingshallen und Sportanlagen in ganz Europa.',
      nl: 'Prestatie onder de voeten. VeldFit-rubbertegels bieden schokabsorptie, anti-slip grip en anti-vermoeidheidcomfort voor fitnesscentra, speelplaatsen, trainingszalen en sportfaciliteiten in heel Europa.',
    },
    products: [
      {
        name: { en: 'Solid Top Gym Tile', ar: 'بلاط الصالة الرياضية بقمة صلبة', de: 'Fliese mit massiver Oberseite für Fitnessstudios', nl: 'Massief bovenkant fitnesstegel' },
        code: 'SPR 12',
        image: spr12,
        desc: {
          en: 'Premium interlocking rubber tile for gymnasiums and exercise areas. The elephant skin surface pattern provides excellent grip during exercise while the bottom studs deliver cushioning to protect knees and ankles. Available with black or yellow edging per safety standards.',
          ar: 'بلاط مطاطي متميز قابل للتشابك لصالات الألعاب الرياضية وأماكن التمارين. يوفر نمط سطح جلد الفيل قبضة ممتازة أثناء التمرين بينما توفر الدبابيس السفلية توسيد لحماية الركبتين والكاحلين. متوفر بحواف سوداء أو صفراء وفقاً لمعايير السلامة.',
          de: 'Premium-Verriegelungsgummifliese für Fitnessstudios und Trainingsbereiche. Das Elefantenhaut-Muster bietet hervorragenden Grip, während die Bodenstifte die Knie und Knöchel schützen. Mit schwarzen oder gelben Kanten nach Sicherheitsnormen erhältlich.',
          nl: 'Premium insteekbare rubbertegel voor fitnessruimten en trainingsgebieden. Het olifantshuidpatroon biedt uitstekende grip, terwijl de bodemstiften knieën en enkels beschermen. Verkrijgbaar met zwarte of gele randen.',
        },
        specs: [
          { key: 'spec_material',    value: { en: 'Natural & recycled rubber blend', ar: 'مزيج مطاط طبيعي ومعاد تدويره', de: 'Natur- & Recyclingkautschukmischung', nl: 'Natuur- en gerecycled rubbermengsel' } },
          { key: 'spec_surface',     value: { en: 'Elephant skin pattern',           ar: 'نمط جلد الفيل',                  de: 'Elefantenhaut-Muster',              nl: 'Olifantshuidpatroon' } },
          { key: 'spec_thickness',   value: { en: '16mm', ar: '16 ملم', de: '16 mm', nl: '16 mm' } },
          { key: 'spec_application', value: { en: 'Gyms, exercise areas, sports halls', ar: 'صالات الرياضة ومناطق التمارين وقاعات الرياضة', de: 'Fitnessstudios, Trainingsbereiche, Sporthallen', nl: 'Fitnessruimten, trainingsgebieden, sporthallen' } },
        ],
        sizes: [
          ['90 × 90 cm × 16mm', '8.7 kg'],
        ],
        badgeKeys: ['badge_recyclable', 'badge_eco', 'badge_antislip', 'badge_anti_fatigue', 'badge_interlocking', 'badge_uv'],
      },
      {
        name: { en: 'Solid Top Multiplier (28mm)', ar: 'مضاعف القمة الصلبة (28 ملم)', de: 'Massiver Oberteil-Multiplikator (28 mm)', nl: 'Massieve bovenkant vermenigvuldiger (28 mm)' },
        code: 'SPR 14',
        image: spr14,
        desc: {
          en: 'Heavy-duty version of the solid top tile — 28mm thick for areas requiring maximum shock absorption, such as free weights zones and functional training areas. Holes at the bottom provide cushioning and comfort underfoot during long training sessions.',
          ar: 'نسخة الخدمة الثقيلة من بلاط القمة الصلبة — سمك 28 ملم للمناطق التي تتطلب أقصى امتصاص للصدمات، مثل مناطق الأوزان الحرة ومناطق التدريب الوظيفي. توفر الثقوب في الأسفل توسيد وراحة تحت القدمين أثناء جلسات التدريب الطويلة.',
          de: 'Heavy-Duty-Version mit 28 mm Dicke für Bereiche mit maximaler Stoßdämpfung — Freihantelbereiche und Funktionstrainingsbereiche. Löcher an der Unterseite bieten Polsterung und Komfort.',
          nl: 'Heavy-duty versie — 28 mm dik voor maximale schokabsorptie in vrije gewichtszones en functionele trainingsgebieden. Gaten aan de onderkant bieden demping en comfort.',
        },
        specs: [
          { key: 'spec_material',    value: { en: 'Natural & recycled rubber blend', ar: 'مزيج مطاط طبيعي ومعاد تدويره', de: 'Natur- & Recyclingkautschukmischung', nl: 'Natuur- en gerecycled rubbermengsel' } },
          { key: 'spec_surface',     value: { en: 'Elephant skin pattern',           ar: 'نمط جلد الفيل',                  de: 'Elefantenhaut-Muster',              nl: 'Olifantshuidpatroon' } },
          { key: 'spec_thickness',   value: { en: '28mm', ar: '28 ملم', de: '28 mm', nl: '28 mm' } },
          { key: 'spec_application', value: { en: 'Gyms, warm-up areas, weight zones', ar: 'صالات الرياضة ومناطق الإحماء ومناطق الأوزان', de: 'Fitnessstudios, Aufwärmbereiche, Gewichtszonen', nl: 'Fitnessruimten, opwarmgebieden, gewichtszones' } },
        ],
        sizes: [
          ['90 × 90 cm × 28mm', '7.8 kg'],
        ],
        badgeKeys: ['badge_recyclable', 'badge_eco', 'badge_28mm', 'badge_heavy_duty', 'badge_interlocking'],
      },
    ],
  },
  {
    id: 'veldguard',
    image: veldguardHero,
    category: { en: 'VeldGuard® — Industrial & Anti-Fatigue Mats', ar: 'VeldGuard® — الحصائر الصناعية ومضادة الإرهاق', de: 'VeldGuard® — Industrie- & Anti-Ermüdungs-Matten', nl: 'VeldGuard® — Industrie- & Anti-Vermoeidheidmatten' },
    subtitle:  { en: 'Workplace Safety', ar: 'سلامة مكان العمل', de: 'Arbeitsplatzsicherheit', nl: 'Werkplaatsveiligheid' },
    intro: {
      en: 'Engineered for demanding environments. VeldGuard mats reduce slip hazards, protect workers from fatigue, and withstand oils, chemicals and fire in workshops, factories, military ranges and service areas.',
      ar: 'مصممة للبيئات الصعبة. تقلل حصائر VeldGuard من مخاطر الانزلاق وتحمي العمال من الإرهاق وتتحمل الزيوت والمواد الكيميائية والنار في ورش العمل والمصانع والنطاقات العسكرية ومناطق الخدمة.',
      de: 'Für anspruchsvolle Umgebungen ausgelegt. VeldGuard-Matten reduzieren Ausrutschgefahr, schützen Arbeiter vor Ermüdung und halten Ölen, Chemikalien und Feuer stand.',
      nl: "Ontworpen voor veeleisende omgevingen. VeldGuard-matten verminderen uitglijdrisico's, beschermen werknemers tegen vermoeidheid en weerstaan olie, chemicaliën en brand.",
    },
    products: [
      {
        name: { en: 'Open Top Anti-Fatigue Mat', ar: 'حصيرة مضادة الإرهاق بقمة مفتوحة', de: 'Anti-Ermüdungs-Matte mit offenem Oberteil', nl: 'Anti-vermoeidheidmat met open bovenkant' },
        code: 'SPR 02',
        image: spr02,
        desc: {
          en: 'A fast-moving anti-fatigue mat for wet and oily working conditions. The anti-slip top provides grip while the open design facilitates fluid drainage. Lightweight, easy to connect with male-female connectors to cover large workshop or service station areas. Fire retardant version available.',
          ar: 'حصيرة سريعة الحركة مضادة للإرهاق لظروف العمل الرطبة والزيتية. يوفر السطح المضاد للانزلاق قبضة بينما يسهل التصميم المفتوح تصريف السوائل. خفيفة الوزن وسهلة التوصيل بموصلات ذكر-أنثى. نسخة مقاومة للحريق متاحة.',
          de: 'Eine schnelle Anti-Ermüdungs-Matte für nasse und ölige Arbeitsbedingungen. Rutschfeste Oberseite mit offenem Design für Flüssigkeitsablauf. Leicht und einfach verbindbar. Feuerbeständige Version erhältlich.',
          nl: 'Een snelle anti-vermoeidheidmat voor natte en olieachtige werkomstandigheden. Anti-slip bovenkant met open ontwerp voor vloeistofafwatering. Licht en eenvoudig verbindbaar. Brandwerende versie beschikbaar.',
        },
        specs: [
          { key: 'spec_material',    value: { en: 'Natural & recycled rubber (Nitrile option)', ar: 'مطاط طبيعي ومعاد تدويره (خيار النيتريل)', de: 'Natur- & Recyclingkautschuk (Nitril-Option)', nl: 'Natuur- en gerecycled rubber (Nitryl optie)' } },
          { key: 'spec_thickness',   value: { en: '12mm / 14mm', ar: '12 ملم / 14 ملم', de: '12 mm / 14 mm', nl: '12 mm / 14 mm' } },
          { key: 'spec_uv',          value: { en: 'Yes (black)', ar: 'نعم (أسود)', de: 'Ja (schwarz)', nl: 'Ja (zwart)' } },
          { key: 'spec_fire',        value: { en: 'Standard: No — FR version: Yes', ar: 'القياسي: لا — نسخة FR: نعم', de: 'Standard: Nein — FR-Version: Ja', nl: 'Standaard: Nee — FR-versie: Ja' } },
          { key: 'spec_application', value: { en: 'Workshops, factories, restaurants', ar: 'ورش العمل والمصانع والمطاعم', de: 'Werkstätten, Fabriken, Restaurants', nl: 'Werkplaatsen, fabrieken, restaurants' } },
        ],
        sizes: [
          ['90 × 90 cm × 12mm', '4.1 kg'],
          ['90 × 90 cm × 14mm', '5.4 kg'],
        ],
        badgeKeys: ['badge_recyclable', 'badge_eco', 'badge_antislip', 'badge_oil_nitrile', 'badge_fire_option', 'badge_connectable'],
      },
      {
        name: { en: 'Capsule Grip Mat (Welding Base)', ar: 'حصيرة Capsule Grip (قاعدة اللحام)', de: 'Kapsel-Griffmatte (Schweißbasis)', nl: 'Capsule Grip Mat (lasbasis)' },
        code: 'SPR 08',
        image: spr08,
        desc: {
          en: 'Recommended as a welding base and for heavy industrial use. The fire retardant version is ideal for welding stations. Capsule stud surface prevents tripping and slipping, providing a safe clean working atmosphere. Oil-resistant Nitrile version also available.',
          ar: 'موصى بها كقاعدة للحام والاستخدام الصناعي الثقيل. نسخة مقاومة للحريق مثالية لمحطات اللحام. يمنع سطح دبابيس الكبسولة من التعثر والانزلاق. توجد أيضاً نسخة Nitrile مقاومة للزيت.',
          de: 'Empfohlen als Schweißbasis und für schwere Industrieanwendungen. Die feuerbeständige Version ist ideal für Schweißstationen. Kapselstiftoberfläche verhindert Stolpern und Ausrutschen. Nitrile-Version erhältlich.',
          nl: 'Aanbevolen als lasbasis en voor zwaar industrieel gebruik. De brandwerende versie is ideaal voor lasstations. Kapselmaat-oppervlak voorkomt struikelen en uitglijden. Nitrylversie ook beschikbaar.',
        },
        specs: [
          { key: 'spec_material',    value: { en: 'Natural & recycled rubber (Nitrile option)', ar: 'مطاط طبيعي ومعاد تدويره (خيار النيتريل)', de: 'Natur- & Recyclingkautschuk (Nitril-Option)', nl: 'Natuur- en gerecycled rubber (Nitryl optie)' } },
          { key: 'spec_surface',     value: { en: 'Capsule studs with anti-slip pattern', ar: 'دبابيس كبسولة بنمط مضاد للانزلاق', de: 'Kapselstifte mit rutschfestem Muster', nl: 'Capsulestiften met anti-slippatroon' } },
          { key: 'spec_thickness',   value: { en: '9mm', ar: '9 ملم', de: '9 mm', nl: '9 mm' } },
          { key: 'spec_application', value: { en: 'Welding stations, military ranges, oily work areas', ar: 'محطات اللحام والنطاقات العسكرية ومناطق العمل الزيتية', de: 'Schweißstationen, Militärbereiche, ölige Arbeitsbereiche', nl: 'Lasstations, militaire gebieden, olieachtige werkgebieden' } },
        ],
        sizes: [
          ['45 × 75 cm', '1.8 kg'],
          ['60 × 90 cm', '3.4 kg'],
          ['90 × 150 cm', '9.7 kg'],
        ],
        badgeKeys: ['badge_recyclable', 'badge_fire_option', 'badge_welding', 'badge_antislip', 'badge_oil_nitrile'],
      },
    ],
  },
  {
    id: 'veldhome',
    image: veldhomeHero,
    category: { en: 'VeldHome® — Domestic & Commercial Mats', ar: 'VeldHome® — حصائر منزلية وتجارية', de: 'VeldHome® — Heim- & Gewerbe-Matten', nl: 'VeldHome® — Thuis- & Commerciële Matten' },
    subtitle:  { en: 'Entrance & Lifestyle', ar: 'المدخل وأسلوب الحياة', de: 'Eingang & Lebensstil', nl: 'Ingang & Levensstijl' },
    intro: {
      en: "The welcoming touch. From entrance halls to hotel lobbies, VeldHome mats keep floors clean, safe and stylish. Available in hollow, coir, cast-iron design, polypropylene and scraper styles for every doorway and staircase.",
      ar: 'اللمسة الترحيبية. من بهوات المدخل إلى فنادق الفنادق، تحافظ حصائر VeldHome على الأرضيات نظيفة وآمنة وأنيقة. متوفرة بأسلوب مجوف وجوز الهند وتصميم الحديد الزهر والبولي بروبيلين وأنماط الكاشطة.',
      de: 'Der willkommene Touch. Von Eingangshallen bis zu Hotellobys halten VeldHome-Matten Böden sauber, sicher und stilvoll. Erhältlich in hohlem, Kokosfaser-, Gusseisen-, Polypropylen- und Kratzerausführungen.',
      nl: "De welkome touch. Van inganghallen tot hotellobby's houden VeldHome-matten vloeren schoon, veilig en stijlvol. Verkrijgbaar in holle, kokosvezel-, gietijzeren-, polypropyleen- en schraapuitvoeringen.",
    },
    products: [
      {
        name: { en: 'Multi-Utility Hollow Mat', ar: 'حصيرة مجوفة متعددة الاستخدامات', de: 'Mehrzweck-Hohlmatte', nl: 'Multi-Purpose Holle Mat' },
        code: 'SPR 22',
        image: spr22,
        desc: {
          en: 'The most versatile mat in the range — used as entrance mats, swimming pool surrounds, play area flooring, muddy field mats and wet area covers. The raised wipers scrape shoes clean while holes drain water and snow instantly. Can be decorated with coloured plastic bristles.',
          ar: 'الحصيرة الأكثر تنوعاً في المجموعة — تُستخدم كحصائر مدخل وأحيطة حمامات السباحة وأرضيات مناطق اللعب وحصائر الحقول الموحلة وأغطية المناطق الرطبة. تكشط ممسحات مرفوعة الأحذية نظيفة بينما تصرف الثقوب الماء والثلج فوراً.',
          de: 'Die vielseitigste Matte — als Eingangsmatten, Poolumrandungen, Spielflächenböden, Feldmatten und Nassbereiche nutzbar. Angehobene Scheibenwischer reinigen Schuhe. Löcher lassen Wasser und Schnee sofort ablaufen.',
          nl: 'De meest veelzijdige mat — gebruikt als ingangsmatten, zwembadommezoomingen, speelbereinen, modderige veldmatten en natte gebiedsbedekkingen. Verheven wisers schuren schoenen schoon, gaten wateren water en sneeuw onmiddellijk af.',
        },
        specs: [
          { key: 'spec_material',    value: { en: 'Natural & recycled rubber blend', ar: 'مزيج مطاط طبيعي ومعاد تدويره', de: 'Natur- & Recyclingkautschukmischung', nl: 'Natuur- en gerecycled rubbermengsel' } },
          { key: 'spec_surface',     value: { en: 'Hollow / open top', ar: 'مجوف / قمة مفتوحة', de: 'Hohl / offene Oberseite', nl: 'Hol / open bovenkant' } },
          { key: 'spec_thickness',   value: { en: '16mm / 22mm', ar: '16 ملم / 22 ملم', de: '16 mm / 22 mm', nl: '16 mm / 22 mm' } },
          { key: 'spec_application', value: { en: 'Entrances, pools, play areas, ski resorts, restaurants', ar: 'المداخل وحمامات السباحة ومناطق اللعب ومنتجعات التزلج والمطاعم', de: 'Eingänge, Pools, Spielbereiche, Skigebiete, Restaurants', nl: 'Ingangen, zwembaden, speelgebieden, skigebieden, restaurants' } },
        ],
        sizes: [
          ['40 × 60 cm (16mm)', '1.0 kg'],
          ['50 × 80 cm (22mm)', '3.0 kg'],
          ['80 × 120 cm (22mm)', '7.0 kg'],
          ['100 × 150 cm (22mm)', '11.2 kg'],
        ],
        badgeKeys: ['badge_recyclable', 'badge_eco', 'badge_antislip', 'badge_drainage', 'badge_multi', 'badge_connectable'],
      },
      {
        name: { en: 'Rubberised Coir Door Mat', ar: 'حصيرة باب من ألياف جوز الهند المطاطية', de: 'Gummierte Kokosfaser-Türmatte', nl: 'Rubberachtige kokosvezel deurmatte' },
        code: 'SPR 65 / SPR 87',
        image: spr65,
        desc: {
          en: 'The eco-conscious choice for European homes. Traditional coir fiber provides natural elegance and aggressive scraping action, while rubber backing enhances durability and stability. The crush-proof fiber surface creates a non-slip top. A sustainable, biodegradable option that resonates strongly with European eco-values.',
          ar: 'الاختيار الواعي بالبيئة للمنازل الأوروبية. توفر ألياف جوز الهند التقليدية أناقة طبيعية وإجراء كشط عدواني، بينما يعزز الدعم المطاطي المتانة والاستقرار. يخلق السطح الليفي المقاوم للسحق جزء علوي مضاد للانزلاق.',
          de: 'Die umweltbewusste Wahl für europäische Häuser. Traditionelle Kokosfasern bieten natürliche Eleganz und aggressive Kratzbewegung. Die gummierte Unterseite verbessert Langlebigkeit. Nachhaltige, biologisch abbaubare Option.',
          nl: 'De milieubewuste keuze voor Europese huizen. Traditionele kokosvezels bieden natuurlijke elegantie en agressieve schraapactie. De rubberachtergrond verbetert duurzaamheid. Duurzame, biologisch afbreekbare optie.',
        },
        specs: [
          { key: 'spec_material',    value: { en: 'Coir fiber top with rubber backing', ar: 'قمة من ألياف جوز الهند مع ظهر مطاطي', de: 'Kokosfaseroberteil mit Gummiunterseite', nl: 'Kokosvezelbovenkant met rubberachtergrond' } },
          { key: 'spec_surface',     value: { en: 'Anti-slip coco finish', ar: 'سطح جوز الهند مضاد للانزلاق', de: 'Rutschfester Kokosoberfläche', nl: 'Anti-slip kokosopp ervlak' } },
          { key: 'spec_thickness',   value: { en: '19mm', ar: '19 ملم', de: '19 mm', nl: '19 mm' } },
          { key: 'spec_application', value: { en: 'Home & commercial entrances', ar: 'مداخل المنازل والمنشآت التجارية', de: 'Heim- & Gewerbeeingänge', nl: 'Thuis- & commerciële ingangen' } },
        ],
        sizes: [
          ['45 × 75 cm', '2.1 kg'],
          ['60 × 90 cm', '3.3 kg'],
          ['60 × 100 cm', '3.3 kg'],
        ],
        badgeKeys: ['badge_recyclable', 'badge_coir', 'badge_biodegradable', 'badge_antislip', 'badge_scraping'],
      },
      {
        name: { en: 'Rub & Eco Poly Mat', ar: 'حصيرة Rub & Eco Poly', de: 'Rub & Eco Poly-Matte', nl: 'Rub & Eco Poly Mat' },
        code: 'SPR 03 / SPR 09',
        image: spr03,
        desc: {
          en: 'Combines polypropylene top with rubber backing for the ultimate entrance mat. Chemical resistant, mildew-proof and weatherproof. Available in 9 colour variants to match any interior. The fibrous surface traps dirt and moisture before it enters the building.',
          ar: 'يجمع بين أعلى البولي بروبيلين وظهر المطاط للحصيرة المدخل النهائية. مقاوم للمواد الكيميائية وضد العفن والعوامل الجوية. متوفر بـ 9 متغيرات لونية. يحبس السطح الليفي الأوساخ والرطوبة قبل دخول المبنى.',
          de: 'Kombiniert Polypropylen-Oberseite mit Gummiunterseite. Chemikalienbeständig, schimmelresistent und wetterfest. Erhältlich in 9 Farbvarianten. Die faserförmige Oberfläche fängt Schmutz und Feuchtigkeit.',
          nl: 'Combineert polypropyleenbovenkant met rubberachtergrond. Chemisch bestendig, schimmelbestendig en weerbestendig. Verkrijgbaar in 9 kleurvarianten. Het vezelachtig oppervlak vangt vuil en vocht op.',
        },
        specs: [
          { key: 'spec_material',    value: { en: 'Polypropylene top / rubber backing', ar: 'قمة بولي بروبيلين / ظهر مطاطي', de: 'Polypropylen-Oberseite / Gummiunterseite', nl: 'Polypropyleenbovenkant / rubberachtergrond' } },
          { key: 'spec_surface',     value: { en: 'Anti-slip fabric finish', ar: 'سطح قماش مضاد للانزلاق', de: 'Rutschfeste Gewebeoberfläche', nl: 'Anti-slip stofopp ervlak' } },
          { key: 'spec_thickness',   value: { en: '9mm', ar: '9 ملم', de: '9 mm', nl: '9 mm' } },
          { key: 'spec_colours',     value: { en: 'Multiple — incl. red, beige, blue, green, grey', ar: 'متعددة — منها الأحمر والبيج والأزرق والأخضر والرمادي', de: 'Mehrere — u. a. Rot, Beige, Blau, Grün, Grau', nl: 'Meerdere — incl. rood, beige, blauw, groen, grijs' } },
        ],
        sizes: [
          ['40 × 60 cm', '1.2 kg'],
          ['60 × 90 cm', '2.7 kg'],
          ['80 × 120 cm', '4.3 kg'],
          ['90 × 150 cm', '5.7 kg'],
        ],
        badgeKeys: ['badge_recyclable', 'badge_eco', 'badge_colours', 'badge_weather', 'badge_antislip'],
      },
    ],
  },
]

// ── Badge label lookup (all 4 languages) ─────────────────────
// If these keys already exist in your translations.js, remove this map
// and just use t.products[key] directly. Otherwise keep this as a fallback.
const BADGE_LABELS = {
  badge_recyclable:   { en: 'Recyclable',                  ar: 'قابل لإعادة التدوير',       de: 'Recycelbar',                  nl: 'Recyclebaar' },
  badge_eco:          { en: 'Eco-Friendly',                 ar: 'صديق للبيئة',               de: 'Umweltfreundlich',            nl: 'Milieuvriendelijk' },
  badge_antislip:     { en: 'Anti-Slip',                    ar: 'مضاد للانزلاق',             de: 'Rutschfest',                  nl: 'Anti-slip' },
  badge_drainage:     { en: 'Water Drainage',               ar: 'تصريف الماء',               de: 'Wasserablauf',                nl: 'Waterafwatering' },
  badge_easy_clean:   { en: 'Easy Clean',                   ar: 'سهل التنظيف',               de: 'Leicht zu reinigen',          nl: 'Gemakkelijk schoon' },
  badge_interlocking: { en: 'Interlocking',                 ar: 'قابل للتشابك',              de: 'Verriegelbar',                nl: 'Insteekbaar' },
  badge_anti_fatigue: { en: 'Anti-Fatigue',                 ar: 'مضاد للإرهاق',              de: 'Anti-Ermüdung',               nl: 'Anti-vermoeidheid' },
  badge_uv:           { en: 'UV Resistant',                 ar: 'مقاوم للأشعة فوق البنفسجية', de: 'UV-beständig',               nl: 'UV-bestendig' },
  badge_25mm:         { en: '25mm Thick',                   ar: 'سماكة 25 ملم',              de: '25 mm Dicke',                 nl: '25 mm dik' },
  badge_hygienic:     { en: 'Hygienic',                     ar: 'صحي',                       de: 'Hygienisch',                  nl: 'Hygiënisch' },
  badge_cobble:       { en: 'Anti-Slip Cobble Surface',     ar: 'سطح حجري مضاد للانزلاق',    de: 'Rutschfeste Kopfsteinpflasteroberfläche', nl: 'Anti-slip keioppervlak' },
  badge_oil_nitrile:  { en: 'Oil Resistant (Nitrile)',      ar: 'مقاوم للزيت (نيتريل)',       de: 'Ölbeständig (Nitril)',         nl: 'Oliebestendig (Nitryl)' },
  badge_fire_option:  { en: 'Fire Retardant Option',        ar: 'خيار مقاوم للحريق',          de: 'Feuerhemmende Option',        nl: 'Brandvertrager optie' },
  badge_connectable:  { en: 'Connectable',                  ar: 'قابل للتوصيل',              de: 'Verbindbar',                  nl: 'Verbindbaar' },
  badge_welding:      { en: 'Welding Base',                  ar: 'قاعدة لحام',                de: 'Schweißbasis',                nl: 'Lasbasis' },
  badge_heavy_duty:   { en: 'Heavy Duty',                   ar: 'خدمة ثقيلة',                de: 'Schwerlast',                  nl: 'Zwaar gebruik' },
  badge_28mm:         { en: '28mm Thick',                   ar: 'سماكة 28 ملم',              de: '28 mm Dicke',                 nl: '28 mm dik' },
  badge_multi:        { en: 'Multi-Purpose',                ar: 'متعدد الاستخدامات',          de: 'Mehrzweck',                   nl: 'Multi-purpose' },
  badge_coir:         { en: 'Natural Coir Fiber',           ar: 'ألياف جوز الهند الطبيعية',  de: 'Natürliche Kokosfaser',       nl: 'Natuurlijke kokosvezels' },
  badge_biodegradable:{ en: 'Biodegradable',                ar: 'قابل للتحلل البيولوجي',      de: 'Biologisch abbaubar',         nl: 'Biologisch afbreekbaar' },
  badge_scraping:     { en: 'Scraping Effect',              ar: 'تأثير الكشط',               de: 'Kratzeffekt',                 nl: 'Schraapeffect' },
  badge_colours:      { en: 'Multiple Colours',             ar: 'ألوان متعددة',              de: 'Mehrere Farben',              nl: 'Meerdere kleuren' },
  badge_weather:      { en: 'Weather Resistant',            ar: 'مقاوم للعوامل الجوية',       de: 'Witterungsbeständig',         nl: 'Weerbestendig' },
}

// ── Spec key labels (all 4 languages) ────────────────────────
const SPEC_LABELS = {
  spec_material:    { en: 'Material',        ar: 'المادة',               de: 'Material',          nl: 'Materiaal' },
  spec_surface:     { en: 'Surface Finish',  ar: 'نوع السطح',            de: 'Oberflächenfinish', nl: 'Oppervlakafwerking' },
  spec_thickness:   { en: 'Thickness',       ar: 'السماكة',              de: 'Dicke',             nl: 'Dikte' },
  spec_application: { en: 'Application',     ar: 'التطبيق',              de: 'Anwendung',         nl: 'Toepassing' },
  spec_uv:          { en: 'UV Resistance',   ar: 'مقاومة الأشعة فوق البنفسجية', de: 'UV-Beständigkeit', nl: 'UV-bestendigheid' },
  spec_fire:        { en: 'Fire Retardant',  ar: 'مقاومة الحريق',        de: 'Feuerhemmung',      nl: 'Brandvertrager' },
  spec_colours:     { en: 'Colours',         ar: 'الألوان',              de: 'Farben',            nl: 'Kleuren' },
}

// ── Page header strings (all 4 languages) ────────────────────
// Add these to your translations.js if you prefer — for now they live here.
const PAGE_STRINGS = {
  nav_brand:  { en: 'VELD FLOORINGS', ar: 'فيلد للأرضيات',   de: 'VELD BODENBELÄGE',  nl: 'VELD VLOEREN' },
  eyebrow:    { en: 'PRODUCT CATALOG', ar: 'كتالوج المنتجات', de: 'PRODUKTKATALOG',     nl: 'PRODUCTCATALOGUS' },
  title:      { en: 'Veld Floorings',  ar: 'فيلد للأرضيات',   de: 'Veld Bodenbeläge',  nl: 'Veld Vloeren' },
  subtitle:   {
    en: 'Sustainable rubber flooring solutions for gyms, farms, industry & homes. Eco-friendly, recyclable, and designed for Europe.',
    ar: 'حلول أرضيات مطاطية مستدامة للصالات الرياضية والمزارع والصناعة والمنازل. صديقة للبيئة وقابلة لإعادة التدوير ومصممة لأوروبا.',
    de: 'Nachhaltige Gummibodenbeläge für Fitnessstudios, Bauernhöfe, Industrie & Heimbereich. Umweltfreundlich, recycelbar und für Europa konzipiert.',
    nl: 'Duurzame rubberoplosingen voor fitnessruimten, boerderijen, industrie & thuis. Milieuvriendelijk, recyclebaar en ontworpen voor Europa.',
  },
}

// ── Helper: pick the right language string ────────────────────
function lang(obj, language) {
  return obj[language] || obj['en'] || ''
}

// ── Product Card ──────────────────────────────────────────────
function ProductCard({ product, index, t, language }) {
  const [ref, inView] = useInView(0.05)
  const [open, setOpen] = useState(false)

  return (
    <div
      ref={ref}
      className={`prod-card ${inView ? 'prod-visible' : ''} ${open ? 'prod-open' : ''}`}
      style={{ '--i': index }}
    >
      <div className="prod-top">
        <div className="prod-img-wrap">
          <img src={product.image} alt={lang(product.name, language)} className="prod-img" loading="lazy" />
          <span className="prod-code-badge">{product.code}</span>
        </div>

        <div className="prod-body">
          <h3 className="prod-name">{lang(product.name, language)}</h3>
          <p className="prod-desc">{lang(product.desc, language)}</p>

          {product.badgeKeys && product.badgeKeys.length > 0 && (
            <div className="prod-badges">
              {product.badgeKeys.map((key, i) => (
                <span key={i} className="badge">
                  {/* Use translations file if key exists there, otherwise fall back to BADGE_LABELS */}
                  {(t.products && t.products[key]) || lang(BADGE_LABELS[key] || { en: key }, language)}
                </span>
              ))}
            </div>
          )}

          <div className="prod-actions">
            <button
              className={`prod-expand-btn ${open ? 'btn-open' : ''}`}
              onClick={() => setOpen(o => !o)}
            >
              <span>{open ? t.products.hide_specs : t.products.view_specs}</span>
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            {/* Enquire → mailto */}
            <a
              className="prod-enquire-link"
              href={`mailto:demalabarkitchen@gmail.com?subject=Enquiry: ${lang(product.name, language)} (${product.code})`}
            >
              {t.products.enquire || 'Enquire'}
              <svg width="12" height="12" viewBox="0 0 16 16" fill="none" style={{marginLeft:5,verticalAlign:'middle'}}>
                <path d="M2 8h12M9 3l5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </a>
          </div>
        </div>
      </div>

      {open && (
        <div className="prod-details">
          <div className="prod-specs">
            <h4>{t.products.tech_specs}</h4>
            <table><tbody>
              {product.specs.map((spec, i) => (
                <tr key={i}>
                  <td className="spec-key">
                    {/* Spec row label: from translations file or SPEC_LABELS map */}
                    {(t.products && t.products[spec.key]) || lang(SPEC_LABELS[spec.key] || { en: spec.key }, language)}
                  </td>
                  <td className="spec-val">{lang(spec.value, language)}</td>
                </tr>
              ))}
            </tbody></table>
          </div>

          {product.sizes.length > 0 && (
            <div className="prod-sizes">
              <h4>{t.products.sizes_weights}</h4>
              <table>
                <thead>
                  <tr>
                    <th>{t.products.size}</th>
                    <th>{t.products.weight}</th>
                  </tr>
                </thead>
                <tbody>
                  {product.sizes.map(([s, w], i) => (
                    <tr key={i}><td>{s}</td><td>{w}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Section ───────────────────────────────────────────────────
function Section({ section, t, language }) {
  const [hRef, hInView] = useInView(0.15)

  return (
    <section className="prod-section" id={section.id}>
      <div className="section-hero" style={{ backgroundImage: `url(${section.image})` }}>
        <div className="section-hero-overlay" />
        <div ref={hRef} className={`section-hero-text ${hInView ? 'hero-text-in' : ''}`}>
          <span className="section-tag">{lang(section.subtitle, language)}</span>
          <h2 className="section-title">{lang(section.category, language)}</h2>
          <p className="section-intro">{lang(section.intro, language)}</p>
        </div>
      </div>
      <div className="prod-list">
        {section.products.map((p, i) => (
          <ProductCard key={p.code} product={p} index={i} t={t} language={language} />
        ))}
      </div>
    </section>
  )
}

// ── Nav / Footer strings (all 4 languages) ───────────────────
const NAV_STRINGS = {
  home:     { en: 'Home',     ar: 'الرئيسية', de: 'Startseite', nl: 'Home' },
  products: { en: 'Products', ar: 'المنتجات',  de: 'Produkte',   nl: 'Producten' },
  contact:  { en: 'Contact',  ar: 'اتصل بنا',  de: 'Kontakt',    nl: 'Contact' },
}

const FOOTER_STRINGS = {
  brand_desc: {
    en: 'Premium industrial flooring and rubber matting solutions engineered for durability, comfort, and performance.',
    ar: 'حلول أرضيات صناعية متميزة وحصائر مطاطية مصممة للمتانة والراحة والأداء.',
    de: 'Hochwertige Industriebodenbeläge und Gummimatten für Langlebigkeit, Komfort und Leistung.',
    nl: 'Premium industriële vloeroplossingen en rubberen matten ontworpen voor duurzaamheid, comfort en prestaties.',
  },
  quick_links: { en: 'Quick Links', ar: 'روابط سريعة', de: 'Schnelllinks',   nl: 'Snelle Links' },
  our_products:{ en: 'Products',    ar: 'المنتجات',    de: 'Produkte',       nl: 'Producten' },
  industrial:  { en: 'Industrial Mats',     ar: 'حصائر صناعية',          de: 'Industriematten',       nl: 'Industriematten' },
  gym:         { en: 'Gym Flooring',        ar: 'أرضيات الجيم',           de: 'Fitnessstudio-Böden',   nl: 'Fitness Vloeren' },
  antifatigue: { en: 'Anti Fatigue Mats',   ar: 'حصائر مضادة للإرهاق',   de: 'Anti-Ermüdungsmatten',  nl: 'Anti-vermoeidheidsmatten' },
  commercial:  { en: 'Commercial Flooring', ar: 'أرضيات تجارية',          de: 'Gewerbeböden',          nl: 'Commerciële Vloeren' },
  copyright:   { en: '© {year} VeldFarm. All Rights Reserved.', ar: '© {year} VeldFarm. جميع الحقوق محفوظة.', de: '© {year} VeldFarm. Alle Rechte vorbehalten.', nl: '© {year} VeldFarm. Alle rechten voorbehouden.' },
}

// ── Contact constants ─────────────────────────────────────────
const CONTACT_EMAIL = 'malabarmatseurope@gmail.com'
const CONTACT_PHONE = '+31 637546423'
const CONTACT_WHATSAPP = 'https://wa.me/+49 176 60373098'

// ── Main Site Navbar ──────────────────────────────────────────
function SiteNav({ language }) {
  const [menuOpen, setMenuOpen] = React.useState(false)
  return (
    <header className="pnav-header">
      {/* Top bar — phone + email */}
      <div className="pnav-topbar">
        <span className="pnav-topbar-item">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.06 1.18 2 2 0 012 0h3a2 2 0 012 1.72c.13 1.02.37 2.03.71 3.01a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.06-1.06a2 2 0 012.11-.45c.98.34 1.99.58 3.01.71A2 2 0 0122 14.92z"/></svg>
          <a href={`tel:${CONTACT_PHONE}`}>{CONTACT_PHONE}</a>
        </span>
        <span className="pnav-topbar-divider" />
        <span className="pnav-topbar-item">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
        </span>
      </div>

      {/* Main nav bar */}
      <nav className="pnav">
        <Link to="/" className="pnav-logo-link">
          <div className="pnav-logo-wrap">
            <img src={logo} alt="Veld Floorings" className="pnav-logo-img" />
          </div>
        </Link>

        {/* Desktop links */}
        <div className="pnav-links">
          <Link to="/">{lang(NAV_STRINGS.home, language)}</Link>
          <Link to="/products" className="pnav-active">{lang(NAV_STRINGS.products, language)}</Link>
          <Link to="/contact">{lang(NAV_STRINGS.contact, language)}</Link>
        </div>

        {/* Desktop CTA cluster */}
        <div className="pnav-cta-cluster">
          <a href={`tel:${CONTACT_PHONE}`} className="pnav-cta-phone">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.06 1.18 2 2 0 012 0h3a2 2 0 012 1.72c.13 1.02.37 2.03.71 3.01a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.06-1.06a2 2 0 012.11-.45c.98.34 1.99.58 3.01.71A2 2 0 0122 14.92z"/></svg>
            {CONTACT_PHONE}
          </a>
          <a href={CONTACT_WHATSAPP} target="_blank" rel="noopener noreferrer" className="pnav-cta-wa">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
          </a>
        </div>

        {/* Mobile hamburger */}
        <button className="pnav-burger" onClick={() => setMenuOpen(o => !o)} aria-label="Menu">
          <span className={menuOpen ? 'burger-open' : ''} />
          <span className={menuOpen ? 'burger-open' : ''} />
          <span className={menuOpen ? 'burger-open' : ''} />
        </button>
      </nav>

      {/* Mobile drawer */}
      <div className={`pnav-drawer ${menuOpen ? 'drawer-open' : ''}`}>
        <Link to="/" onClick={() => setMenuOpen(false)}>{lang(NAV_STRINGS.home, language)}</Link>
        <Link to="/products" className="pnav-active" onClick={() => setMenuOpen(false)}>{lang(NAV_STRINGS.products, language)}</Link>
        <Link to="/contact" onClick={() => setMenuOpen(false)}>{lang(NAV_STRINGS.contact, language)}</Link>
        <div className="drawer-cta-row">
          <a href={`tel:${CONTACT_PHONE}`} className="drawer-phone">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.06 1.18 2 2 0 012 0h3a2 2 0 012 1.72c.13 1.02.37 2.03.71 3.01a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.06-1.06a2 2 0 012.11-.45c.98.34 1.99.58 3.01.71A2 2 0 0122 14.92z"/></svg>
            Call Us
          </a>
          <a href={CONTACT_WHATSAPP} target="_blank" rel="noopener noreferrer" className="drawer-wa">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            WhatsApp
          </a>
        </div>
      </div>
    </header>
  )
}

// ── Footer ────────────────────────────────────────────────────
function SiteFooter({ language }) {
  const year = new Date().getFullYear()
  const copyright = lang(FOOTER_STRINGS.copyright, language).replace('{year}', year)
  return (
    <footer className="pfooter">
      {/* Top accent bar */}
      <div className="pfooter-accent-bar" />

      <div className="pfooter-inner">

        {/* Brand column */}
        <div className="pfooter-brand">
          <Link to="/" className="pnav-logo-link">
            <div className="pnav-logo-wrap pfooter-logo-wrap">
              <img src={logo} alt="Veld Floorings" className="pnav-logo-img" />
            </div>
          </Link>
          <p>{lang(FOOTER_STRINGS.brand_desc, language)}</p>
          {/* Social / contact chips */}
          <div className="pfooter-chips">
            <a href={CONTACT_WHATSAPP} target="_blank" rel="noopener noreferrer" className="pfooter-chip pfooter-chip-wa">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              WhatsApp
            </a>
            <a href={`mailto:${CONTACT_EMAIL}`} className="pfooter-chip pfooter-chip-mail">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
              Email
            </a>
          </div>
        </div>

        {/* Quick links */}
        <div className="pfooter-links">
          <h4>{lang(FOOTER_STRINGS.quick_links, language)}</h4>
          <ul>
            <li><Link to="/">{lang(NAV_STRINGS.home, language)}</Link></li>
            <li><Link to="/products">{lang(NAV_STRINGS.products, language)}</Link></li>
            <li><Link to="/contact">{lang(NAV_STRINGS.contact, language)}</Link></li>
          </ul>
        </div>

        {/* Products list */}
        <div className="pfooter-links">
          <h4>{lang(FOOTER_STRINGS.our_products, language)}</h4>
          <ul>
            <li><Link to="/products">{lang(FOOTER_STRINGS.industrial, language)}</Link></li>
            <li><Link to="/products">{lang(FOOTER_STRINGS.gym, language)}</Link></li>
            <li><Link to="/products">{lang(FOOTER_STRINGS.antifatigue, language)}</Link></li>
            <li><Link to="/products">{lang(FOOTER_STRINGS.commercial, language)}</Link></li>
          </ul>
        </div>

        {/* Contact column */}
        <div className="pfooter-contact-col">
          <h4>Contact</h4>
          <div className="pfooter-contact-item">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#4caf85" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.06 1.18 2 2 0 012 0h3a2 2 0 012 1.72c.13 1.02.37 2.03.71 3.01a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.06-1.06a2 2 0 012.11-.45c.98.34 1.99.58 3.01.71A2 2 0 0122 14.92z"/></svg>
            <a href={`tel:${CONTACT_PHONE}`}>{CONTACT_PHONE}</a>
          </div>
          <div className="pfooter-contact-item">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#4caf85" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
          </div>
          <a href={CONTACT_WHATSAPP} target="_blank" rel="noopener noreferrer" className="pfooter-wa-btn">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            Chat on WhatsApp
          </a>
        </div>

      </div>

      {/* Bottom bar */}
      <div className="pfooter-bottom">
        <span>{copyright}</span>
        <div className="pfooter-bottom-links">
          <a href={`mailto:${CONTACT_EMAIL}`}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
          </a>
          <a href={CONTACT_WHATSAPP} target="_blank" rel="noopener noreferrer">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
          </a>
          <a href={`tel:${CONTACT_PHONE}`}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.06 1.18 2 2 0 012 0h3a2 2 0 012 1.72c.13 1.02.37 2.03.71 3.01a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.06-1.06a2 2 0 012.11-.45c.98.34 1.99.58 3.01.71A2 2 0 0122 14.92z"/></svg>
          </a>
        </div>
      </div>
    </footer>
  )
}

// ── Page ──────────────────────────────────────────────────────
function Products() {
  const { language } = useLanguage()
  const t = translations[language] || translations.en

  const [active, setActive] = useState('veldfarm')

  const navItems = [
    { id: 'veldfarm',  label: 'VeldFarm®' },
    { id: 'veldfit',   label: 'VeldFit®' },
    { id: 'veldguard', label: 'VeldGuard®' },
    { id: 'veldhome',  label: 'VeldHome®' },
  ]

  const scrollTo = (id) => {
    setActive(id)
    // account for the two fixed/sticky bars (site nav 80px + category nav 60px)
    const el = document.getElementById(id)
    if (!el) return
    const top = el.getBoundingClientRect().top + window.scrollY - 148
    window.scrollTo({ top, behavior: 'smooth' })
  }

  useEffect(() => {
    const handler = () => {
      for (const { id } of [...navItems].reverse()) {
        const el = document.getElementById(id)
        if (!el) continue
        if (el.getBoundingClientRect().top <= 160) { setActive(id); break }
      }
    }
    window.addEventListener('scroll', handler, { passive: true })
    return () => window.removeEventListener('scroll', handler)
  }, [])

  return (
    <div className="products-page" dir={language === 'ar' ? 'rtl' : 'ltr'}>

      {/* ── Main site nav ── */}
      <SiteNav language={language} />

      {/* ── Sticky category nav (sits below site nav) ── */}
      <nav className="prod-nav">
        <span className="prod-nav-label">{lang(PAGE_STRINGS.nav_brand, language)}</span>
        <div className="prod-nav-tabs">
          {navItems.map(({ id, label }) => (
            <button
              key={id}
              className={`prod-nav-tab ${active === id ? 'tab-active' : ''}`}
              onClick={() => scrollTo(id)}
            >
              {label}
            </button>
          ))}
        </div>
      </nav>

      {/* ── Page header ── */}
      <div className="prod-page-header">
        <span className="prod-page-eyebrow">{lang(PAGE_STRINGS.eyebrow, language)}</span>
        <h1 className="prod-page-title">{lang(PAGE_STRINGS.title, language)}</h1>
        <p className="prod-page-sub">{lang(PAGE_STRINGS.subtitle, language)}</p>
      </div>

      {/* ── Product sections ── */}
      {sections.map(s => (
        <Section key={s.id} section={s} t={t} language={language} />
      ))}

      {/* ── Contact CTA strip ── */}
      <div className="contact-cta-strip">
        <span className="contact-cta-strip-label">Get In Touch</span>
        <h2>Ready to Order?</h2>
        <p>Contact our team for pricing, samples and custom sizes. We respond within 24 hours.</p>
        <div className="contact-cta-btns">
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="cta-btn-email"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
            Send Us a Message
          </a>
          <a
            href={`tel:${CONTACT_PHONE}`}
            className="cta-btn-phone"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.06 1.18 2 2 0 012 0h3a2 2 0 012 1.72c.13 1.02.37 2.03.71 3.01a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.06-1.06a2 2 0 012.11-.45c.98.34 1.99.58 3.01.71A2 2 0 0122 14.92z"/></svg>
            {CONTACT_PHONE}
          </a>
          <a
            href={CONTACT_WHATSAPP}
            target="_blank"
            rel="noopener noreferrer"
            className="cta-btn-wa"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            WhatsApp Us
          </a>
        </div>
      </div>

      {/* ── Footer ── */}
      <SiteFooter language={language} />

    </div>
  )
}

export default Products